/* eslint-disable no-param-reassign */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import useParser, {
  getExpressionFields,
  expressionFulfillsPatternTemplate,
  isNumber,
} from 'apps/web_forms/expressions/expressions';
import { fieldsShareSameParent } from 'apps/web_forms/expressions/utils';
import {
  STRINGS_REGEX,
  IS_STRING,
  splitBySeparators,
  getSpecificFieldIdentifierRegex,
  escapeRegExp,
} from 'apps/web_forms/expressions/regularExpressions';
import {
  updateInitialAnswers,
  removeInitialAnswers,
  updateAllRules,
} from 'apps/web_forms/slices/formSlice';
import { expressionNames } from 'apps/web_forms/utils/constants';
import {
  getFormattedDate,
  isDateTimeOrDatetimeValid,
  removeTimeZoneFormatted,
} from 'apps/web_forms/utils/dateTimeFormatter';
import { getIdWithoutSuffix } from 'apps/web_forms/utils/inputsUtils';
import { useSelector } from 'react-redux';
import {
  selectFieldsToWatch,
  selectRepeatGroupFields,
  selectGroupList,
  selectSelectFieldResources,
  selectResourceVersions,
  selectInitialAnswers,
  selectFieldErrors,
  selectRepeatGroupList,
  selectSubforms,
  selectRules,
  selectResourceLoadCounter,
  selectFieldsWithExpressions,
} from 'apps/web_forms/hooks/useGlobalSelector';

const functionsThatTakeFieldIdentifier = [
  'COUNT',
  'TIMESTAMP',
  'GEOSTAMP',
  'INDEX_OF',
];
const functionsThatTakeAllRepeatGroupValues = ['SUM', 'AVERAGE', 'CONCATENATE'];
const functionsThatShouldKeepStringFormatting = [
  'LENGTH',
  'CONCATENATE',
  'LOWERCASE',
  'UPPERCASE',
  'CONTAINS',
  'SUBSTRING',
  'ISBLANK',
  'NOTBLANK',
];

// TODO: another candidate for significant refactoring/rearchitecting. Expressions in general.
function useExpression({ getValues, getInput, elements, formContext }) {
  const fieldsWithExpressions = useSelector(selectFieldsWithExpressions);
  const fieldsToWatchMap = useSelector(selectFieldsToWatch);
  const reduxRepeatGroupFields = useSelector(selectRepeatGroupFields);
  const groupList = useSelector(selectGroupList);
  const selectFieldResources = useSelector(selectSelectFieldResources);
  const resourceVersions = useSelector(selectResourceVersions);
  const initialAnswers = useSelector(selectInitialAnswers);
  const fieldErrors = useSelector(selectFieldErrors);
  const groups = useSelector(selectRepeatGroupList);
  const subforms = useSelector(selectSubforms);
  const rules = useSelector(selectRules);
  const resourceLoadCounter = useSelector(selectResourceLoadCounter);
  const parse = useParser();

  const repeatGroupFieldsRef = useRef({});
  const groupListRef = useRef({});
  const selectFieldResourcesRef = useRef({});
  const resourceVersionsRef = useRef({});
  const fieldErrorsRef = useRef({});
  const groupsRef = useRef([]);
  const subformsRef = useRef([]);
  const [refreshedGroupElements, setRefreshedGroupElements] = useState([]);
  const [renderIterationNumber, setRenderIterationNumber] = useState(1);
  const [fieldsWithInitialAnswerExpr, setFieldsWithInitialAnswerExpr] = useState([]);
  const pendingRulesUpdates = useRef({});
  const [triggerUpdate, setTriggerUpdate] = useState(Date.now());

  const dispatch = useDispatch();

  const [repeatGroups, setRepeatGroups] = useState(reduxRepeatGroupFields);

  const memoizedRules = useMemo(() => rules, [rules]);

  // useEffect hooks aren't getting these reduxRepeatGroupFields and selectFieldResources data
  // updated, so this is a workaround for that.
  useEffect(() => {
    repeatGroupFieldsRef.current = reduxRepeatGroupFields;
    setRepeatGroups(reduxRepeatGroupFields);
  }, [reduxRepeatGroupFields]);
  useEffect(() => {
    groupListRef.current = groupList;
  }, [groupList]);
  useEffect(() => {
    selectFieldResourcesRef.current = selectFieldResources;
  }, [selectFieldResources]);
  useEffect(() => {
    resourceVersionsRef.current = resourceVersions;
  }, [resourceVersions]);
  useEffect(() => {
    fieldErrorsRef.current = fieldErrors;
  }, [fieldErrors]);
  useEffect(() => {
    groupsRef.current = groups;
  }, [groups]);
  useEffect(() => {
    subformsRef.current = subforms;
  }, [subforms]);

  useEffect(function lookForInitialAnswerExpressions() {
    const fieldsWithInitialAnswerExprToSet = iterateElements(elements);
    setFieldsWithInitialAnswerExpr(fieldsWithInitialAnswerExprToSet);
  }, []);
  
  const iterateElements = (elements) => {
    let fieldsWithInitialAnswerExpr = [];
    elements.forEach((el) => {
      if (el.children) {
        fieldsWithInitialAnswerExpr = [...fieldsWithInitialAnswerExpr, ...iterateElements(el.children)];
      } else if (el.initialAnswerExpr) {
        fieldsWithInitialAnswerExpr.push(el);
      }
    });
    return fieldsWithInitialAnswerExpr;
  };


  //Trying a different approach to solve the problem of the expressions being updated after the initial render
  //this batches the rules that you want to update and then updates them all at once
  //also since it gets triggered by onChangeValue it may update more than once the Redux state
  const updateRules = useCallback((rules) => {
    pendingRulesUpdates.current = {...pendingRulesUpdates.current, ...rules};
    setTriggerUpdate(Date.now()); // Toggle the state to trigger the update
  }, []);
  
  useEffect(() => {
    if (Object.keys(pendingRulesUpdates.current).length > 0) {
      dispatch(updateAllRules({rules: pendingRulesUpdates.current}));
      pendingRulesUpdates.current = {};
    }
  }, [triggerUpdate]);
  
  /**
   * Evaluate an expression
   * values should be a Map with following structure: (this change depending on the expression)
   * {
   *   "Yes_No_Question": true,
   *   "Free_Text_Question": "My text value"
   * }
   */
  const evaluateExpression = useCallback(
    ({ values, targetIdentifier, targetField, expression, isInitialAnswer }) => {
      const { type_derived: typeDerived, type } = targetField;
      let newExpression = prepareExpression(expression, values);
      let result = parseExpression(newExpression, {
        targetIdentifier,
        targetField,
        isInitialAnswer,
        values
      });
  
      return processResultValue(result, typeDerived || type);
    },
    [resourceVersionsRef.current]
  );
  
  function prepareExpression(expression, values) {
    const expressionFields = getExpressionFields(expression);
    return expressionFields.reduce((currentExpression, exprFieldName) => {
      if (!isFieldInValues(exprFieldName, values)) {
        return currentExpression;
      }
  
      let exprValue = getExpressionValue(exprFieldName, values, currentExpression);
      return replaceExpressionValue(currentExpression, exprFieldName, exprValue);
    }, expression);
  }
  
  function isFieldInValues(fieldName, values) {
    return Object.keys(values).some(key => getIdWithoutSuffix(key) === fieldName);
  }
  
  function getExpressionValue(fieldName, values, expression) {
    const currentWrapperFunction = getCurrentWrapperFunction(expression, fieldName);
    const shouldKeepStringFormatting =
      functionsThatShouldKeepStringFormatting.includes(currentWrapperFunction);

    if (functionsThatTakeFieldIdentifier.includes(currentWrapperFunction)) {
      return fieldName;
    }
  
    let value = values[fieldName];
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '""';
      } else if (functionsThatTakeAllRepeatGroupValues.includes(currentWrapperFunction)) {
        return value
          .map((item) =>
            filterExpressionValue(item, {
              shouldKeepStringFormatting,
            })
          )
          .join(',');
      } else {
        return filterExpressionValue(value[0], { shouldKeepStringFormatting }); // Assuming to take the first value
      }
    } else {
      return filterExpressionValue(value, { shouldKeepStringFormatting });
    }
  }
  
  function replaceExpressionValue(expression, fieldName, value) {
    const exprWithoutStrings = expression.replace(STRINGS_REGEX, `[string]`);
    const exprWithValues = exprWithoutStrings.includes('INDEX_OF') || exprWithoutStrings.includes('COUNT')
      ? exprWithoutStrings
      : exprWithoutStrings.replace(getSpecificFieldIdentifierRegex(fieldName), `$1${value}`);
  
    const exprStrings = expression.match(STRINGS_REGEX);
    if (exprStrings) {
      let i = -1;
      return exprWithValues.replace(/\[string\]/g, () => {
        i += 1;
        return exprStrings[i];
      });
    } else {
      return exprWithValues;
    }
  }
  
  function parseExpression(expression, { targetIdentifier, targetField, isInitialAnswer, values }) {
    try {
      if (expression.includes('INDEX_OF')) {
        return doIndexOfCalculations(expression, targetIdentifier, resourceVersionsRef, targetField);
      } else if (expression.includes('INDEX')) {
        if (isInitialAnswer && Object.keys(values).length === 0) {
          return '';
        } else {
          return parse(expression, targetIdentifier, resourceVersionsRef.current);
        }
      } else if (expression.includes('COUNT')) {
        return doCalculationsForCountExpression(expression, targetIdentifier, resourceVersionsRef, targetField);
      } else if (expression.match(/\[([0]|[1-9]*)\]\//)) {
        return parseGroupExpression(expression, targetIdentifier, resourceVersionsRef, targetField);
      } else if (!expression.match(IS_STRING)) {
        return parse(expression, targetIdentifier, resourceVersionsRef.current);
      } else {
        try {
          return parse(expression, targetIdentifier, resourceVersionsRef.current);
        } catch (error) {
          console.error(error);
          return expression;
        }
      }
    } catch (err) {
      console.log('There was an error with parsing the expression', err);
      return null;
    }
  }

  function parseGroupExpression(
    newExpression,
    targetIdentifier,
    resourceVersionsRef,
    targetField
  ) {
    let resolvedExpression = newExpression;
    if (resolvedExpression.match(/\(([^()]+)\)/g)) {
      resolvedExpression.replace(/\(([^()]+)\)/g, (fullMatch, exactMatch) => {
        let fullMatchWithoutBrackets = fullMatch.slice(1, -1);
        if (fullMatchWithoutBrackets.includes(',')) {
          let stArray = fullMatchWithoutBrackets.split(',');
          stArray.map((group) => {
            if (group.includes(']/')) {
              let groupIdentifierWithoutIndex = group.replace(/\[(.*)\]/, '');
              let groupIndex =
                group.match(/([^[]+(?=]))/) !== null
                  ? group.match(/([^[]+(?=]))/)[0]
                  : '';
              const repeatGroupFields = repeatGroupFieldsRef.current;
              if (repeatGroupFields[groupIdentifierWithoutIndex]) {
                let expressionValue = getGroupValue(
                  groupIdentifierWithoutIndex,
                  targetIdentifier,
                  groupIndex
                );
                resolvedExpression = resolvedExpression.replace(
                  group,
                  expressionValue
                );
              } else {
                resolvedExpression = resolvedExpression.replace(group, '""');
              }
            }
          });
        } else {
          let group = fullMatch.slice(1, -1);
          let groupIdentifierWithoutIndex = group.replace(/\[(.*)\]/, '');
          let groupIndex =
            group.match(/([^[]+(?=]))/) !== null
              ? group.match(/([^[]+(?=]))/)[0]
              : '';
          const repeatGroupFields = repeatGroupFieldsRef.current;
          if (repeatGroupFields[groupIdentifierWithoutIndex]) {
            let expressionValue = getGroupValue(
              groupIdentifierWithoutIndex,
              targetIdentifier,
              groupIndex
            );
            resolvedExpression = resolvedExpression.replace(
              group,
              expressionValue
            );
          } else {
            resolvedExpression = resolvedExpression.replace(group, '""');
          }
        }
      });
    } else {
      let groupIdentifierWithoutIndex = newExpression.replace(/\[(.*)\]/, '');
      let groupIndex =
        newExpression.match(/([^[]+(?=]))/) !== null
          ? newExpression.match(/([^[]+(?=]))/)[0]
          : '';
      const repeatGroupFields = repeatGroupFieldsRef.current;
      if (repeatGroupFields[groupIdentifierWithoutIndex]) {
        let expressionValue = getGroupValue(
          groupIdentifierWithoutIndex,
          targetIdentifier,
          groupIndex
        );
        return expressionValue !== undefined ? expressionValue : '';
      } else {
        return '';
      }
    }

    let result = resolvedExpression;
    /** Enters here only if expression is not resolved yet, or if there are still more calculations to be done */
    if (isDateTimeOrDatetimeValid(result)) {
      /** Only for string that are of the format time/date/datetime will enter here*/
      return removeTimeZoneFormatted(result);
    }

    if (
      isNaN(result) &&
      (result.match(/\((.*)\)/) ||
        result.match(
          /([-+]?[0-9]*\.?[0-9]+[\/\+\-\*])+([-+]?[0-9]*\.?[0-9]+)/
        )) &&
      !isDateTimeInput(targetField)
    ) {
      result = parse(result, targetIdentifier, resourceVersionsRef.current);
    }
    return result;
  }

  function doIndexOfCalculations(
    newExpression,
    targetIdentifier,
    resourceVersionsRef,
    targetField
  ) {
    let result;
    const indexOfRegex = /INDEX_OF\(([^)]+)\)/g;

    /**
     * The INDEX_OF expression can have a different use as other expressions
     * If the expression is used withing a Group to get a value, or within another expression,
     * then there are some steps needed to parse the expression correctly
     */

    /**1. Replace all values of INDEX_OF function with their results*/
    let resolvedExpression = newExpression.replace(
      indexOfRegex,
      (fullMatch, exactMatch) => {
        let strippedIndexOfExpression = fullMatch;
        let value = parse(
          strippedIndexOfExpression,
          targetIdentifier,
          resourceVersionsRef.current
        );
        return JSON.stringify(value);
      }
    );
    /**2. Replace all values inside square brackets with their results
     * if it has square brackets it means that it will return a value of a field inside a Group
     */
    if (resolvedExpression.match(/([^[]+(?=]))/)) {
      let calculatedGroupIndexes = [];
      let insideSquareBracketsRegex = /([^[]+(?=]))/g;

      //Replaces the value inside the brackets with the Group index
      resolvedExpression = resolvedExpression.replace(
        insideSquareBracketsRegex,
        (fullMatch, exactMatch) => {
          let strippedinsideSquareBracketsExpression = fullMatch;
          let calculatedIndex = parse(
            strippedinsideSquareBracketsExpression,
            targetIdentifier,
            resourceVersionsRef.current
          );
          if (calculatedIndex < 0) {
            return '';
          } else {
            calculatedGroupIndexes.push(calculatedIndex);
            return calculatedIndex;
          }
        }
      );
      /**If the expression is a merge of expressions like
       * CONCATENATE(Group[INDEX_OF(Free_Text_Question) - 1]/Free_Text_Question,":",INDEX_OF(Calculated_Index_Question))
       * then the following code will get the value of Group[INDEX_OF(Free_Text_Question) - 1]/Free_Text_Question
       * and replace it to the expression
       */
      let groupsToReplace = resolvedExpression.match(/\(([^()]+)\)/g);
      if (groupsToReplace) {
        resolvedExpression.replace(/\(([^()]+)\)/g, (fullMatch, exactMatch) => {
          let fullMatchWithoutBrackets = fullMatch.slice(1, -1);
          if (fullMatchWithoutBrackets.includes(',')) {
            let stArray = fullMatchWithoutBrackets.split(',');
            stArray.map((group) => {
              if (group.includes(']/')) {
                let groupIdentifierWithoutIndex = group.replace(/\[(.*)\]/, '');
                let groupIndex =
                  group.match(/([^[]+(?=]))/) !== null
                    ? group.match(/([^[]+(?=]))/)[0]
                    : '';
                const repeatGroupFields = repeatGroupFieldsRef.current;
                if (
                  repeatGroupFields[groupIdentifierWithoutIndex] &&
                  calculatedGroupIndexes.length > 0
                ) {
                  let expressionValue = getGroupValue(
                    groupIdentifierWithoutIndex,
                    targetIdentifier,
                    groupIndex
                  );
                  resolvedExpression = resolvedExpression.replace(
                    group,
                    expressionValue
                  );
                } else {
                  resolvedExpression = resolvedExpression.replace(group, '""');
                }
              }
            });
          } else {
            let group = fullMatch.slice(1, -1);
            let groupIdentifierWithoutIndex = group.replace(/\[(.*)\]/, '');
            let groupIndex =
              group.match(/([^[]+(?=]))/) !== null
                ? group.match(/([^[]+(?=]))/)[0]
                : '';
            const repeatGroupFields = repeatGroupFieldsRef.current;
            if (
              repeatGroupFields[groupIdentifierWithoutIndex] &&
              calculatedGroupIndexes.length > 0
            ) {
              let expressionValue = getGroupValue(
                groupIdentifierWithoutIndex,
                targetIdentifier,
                groupIndex
              );
              resolvedExpression = resolvedExpression.replace(
                group,
                expressionValue
              );
            } else {
              resolvedExpression = resolvedExpression.replace(group, '""');
            }
          }
        });
      } else {
        /**
         * If the expression is like Group[INDEX_OF(Free_Text_Question) - 1]/Free_Text_Question
         */
        //get value from redux saved repeatGroupFields
        let groupIdentifierWithoutIndex = resolvedExpression.replace(
          /\[(.*)\]/,
          ''
        );
        let calculatedIndex =
          resolvedExpression.match(/([^[]+(?=]))/) !== null
            ? resolvedExpression.match(/([^[]+(?=]))/)[0]
            : '';
        if (calculatedIndex.length > 0 && !isNaN(calculatedIndex)) {
          resolvedExpression = getGroupValue(
            groupIdentifierWithoutIndex,
            targetIdentifier,
            calculatedIndex
          );
        } else {
          resolvedExpression = '';
        }
      }
    }
    result = resolvedExpression;
    if (isDateTimeOrDatetimeValid(result)) {
      /** Only for string that are of the format time/date/datetime will enter here*/
      return removeTimeZoneFormatted(result);
    }

    if (
      isNaN(result) &&
      (result.match(/\((.*)\)/) ||
        result.match(
          /([-+]?[0-9]*\.?[0-9]+[\/\+\-\*])+([-+]?[0-9]*\.?[0-9]+)/
        )) &&
      !isDateTimeInput(targetField)
    ) {
      /** Enters here only if expression is not resolved yet, or if there are still more calculations to be done */
      result = parse(result, targetIdentifier, resourceVersionsRef.current);
    }
    return result;
  }

  function doCalculationsForCountExpression(
    newExpression,
    targetIdentifier,
    resourceVersionsRef,
    targetField
  ) {
    let result;
    const indexOfRegex = /COUNT\(([^)]+)\)/g;
    /**
     * The COUNT expression can have a different use as other expressions
     * If the expression is used withing a Group to get a value, or within another expression,
     * then there are some steps needed to parse the expression correctly
     */

    /**1. Replace all values of INDEX_OF function with their results*/
    let resolvedExpression = newExpression.replace(
      indexOfRegex,
      (fullMatch, exactMatch) => {
        let strippedIndexOfExpression = fullMatch;
        let value = parse(
          strippedIndexOfExpression,
          targetIdentifier,
          resourceVersionsRef.current
        );
        return JSON.stringify(value);
      }
    );
    /**2. Replace all values inside square brackets with their results
     * if it has square brackets it means that it will return a value of a field inside a Group
     */
    if (resolvedExpression.match(/([^[]+(?=]))/)) {
      let calculatedGroupIndexes = [];
      let insideSquareBracketsRegex = /([^[]+(?=]))/g;

      //Replaces the value inside the brackets with the Group index
      resolvedExpression = resolvedExpression.replace(
        insideSquareBracketsRegex,
        (fullMatch, exactMatch) => {
          let strippedinsideSquareBracketsExpression = fullMatch;
          let calculatedIndex = parse(
            strippedinsideSquareBracketsExpression,
            targetIdentifier,
            resourceVersionsRef.current
          );

          if (calculatedIndex < 0) {
            // calculatedGroupIndexes.push("")
            return '';
          } else {
            calculatedGroupIndexes.push(calculatedIndex);
            return calculatedIndex;
          }
        }
      );

      /**If the expression is a merge of expressions like
       * CONCATENATE(Group[COUNT(Free_Text_Question) - 1]/Free_Text_Question,":",COUNT(Calculated_Index_Question))
       * then the following code will get the value of Group[COUNT(Free_Text_Question) - 1]/Free_Text_Question
       * and replace it to the expression
       */
      let groupsToReplace = resolvedExpression.match(/\(([^()]+)\)/g);
      if (groupsToReplace) {
        resolvedExpression.replace(/\(([^()]+)\)/g, (fullMatch, exactMatch) => {
          let fullMatchWithoutBrackets = fullMatch.slice(1, -1);
          if (fullMatchWithoutBrackets.includes(',')) {
            let stArray = fullMatchWithoutBrackets.split(',');
            stArray.map((group) => {
              if (group.includes(']/')) {
                let groupIdentifierWithoutIndex = group.replace(/\[(.*)\]/, '');
                let groupIndex =
                  group.match(/([^[]+(?=]))/) !== null
                    ? group.match(/([^[]+(?=]))/)[0]
                    : '';
                const repeatGroupFields = repeatGroupFieldsRef.current;
                if (
                  repeatGroupFields[groupIdentifierWithoutIndex] &&
                  calculatedGroupIndexes.length > 0
                ) {
                  let expressionValue = getGroupValue(
                    groupIdentifierWithoutIndex,
                    targetIdentifier,
                    groupIndex
                  );
                  resolvedExpression = resolvedExpression.replace(
                    group,
                    expressionValue
                  );
                } else {
                  resolvedExpression = resolvedExpression.replace(group, '""');
                }
              }
            });
          } else {
            let group = fullMatch.slice(1, -1);
            let groupIdentifierWithoutIndex = group.replace(/\[(.*)\]/, '');
            let groupIndex =
              group.match(/([^[]+(?=]))/) !== null
                ? group.match(/([^[]+(?=]))/)[0]
                : '';
            const repeatGroupFields = repeatGroupFieldsRef.current;
            if (
              repeatGroupFields[groupIdentifierWithoutIndex] &&
              calculatedGroupIndexes.length > 0
            ) {
              let expressionValue = getGroupValue(
                groupIdentifierWithoutIndex,
                targetIdentifier,
                groupIndex
              );
              resolvedExpression = resolvedExpression.replace(
                group,
                expressionValue
              );
            } else {
              resolvedExpression = resolvedExpression.replace(group, '""');
            }
          }
        });
      } else {
        /**
         * If the expression is like Group[COUNT(Free_Text_Question) - 1]/Free_Text_Question
         */
        //get value from redux saved repeatGroupFields
        let groupIdentifierWithoutIndex = resolvedExpression.replace(
          /\[(.*)\]/,
          ''
        );
        let calculatedIndex =
          resolvedExpression.match(/([^[]+(?=]))/) !== null
            ? resolvedExpression.match(/([^[]+(?=]))/)[0]
            : '';
        if (calculatedIndex.length > 0 && !isNaN(calculatedIndex)) {
          resolvedExpression = getGroupValue(
            groupIdentifierWithoutIndex,
            targetIdentifier,
            calculatedIndex
          );
        } else {
          resolvedExpression = '';
        }
      }
    }
    result = resolvedExpression;

    if (isDateTimeOrDatetimeValid(result)) {
      /** Only for string that are of the format time/date/datetime will enter here*/
      return removeTimeZoneFormatted(result);
    }

    if (
      isNaN(result) &&
      (result.match(/\((.*)\)/) ||
        result.match(
          /([-+]?[0-9]*\.?[0-9]+[\/\+\-\*])+([-+]?[0-9]*\.?[0-9]+)/
        )) &&
      !isDateTimeInput(targetField)
    ) {
      /** Enters here only if expression is not resolved yet */
      result = parse(result, targetIdentifier, resourceVersionsRef.current);
    }
    return result;
  }

  function getGroupValue(identifier, containerFieldIdentifier, index) {
    if (containerFieldIdentifier) {
      const repeatGroupFields = repeatGroupFieldsRef.current;
      const containerFieldIdentifiers = containerFieldIdentifier.split('|');
      const splittedContainerIdentifiers = containerFieldIdentifiers
        .slice(0, containerFieldIdentifiers.length - 1)
        .join('|');
      const filteredIdentifiers = repeatGroupFields[identifier]?.filter(
        (groupField) => groupField.includes(splittedContainerIdentifiers)
      );

      /**Added the variable dependent field as a workaround for inputs having expressions like .Group[0]/Date_Questions
       * dependent on inputs with expression NOW()
       */
      const dependentField = getInput(identifier);
      
      if (
        filteredIdentifiers &&
        filteredIdentifiers[index] !== undefined
      ) {
        const value = dependentField.initialAnswerExpr === 'NOW()'
          ? getFormattedDate(new Date(), dependentField.type_derived)
          : getValues(filteredIdentifiers[index]);

        if(filteredIdentifiers[index] !== value) {
          return value ?? '""';
        }
      }
      return '""';
    }
  }

  function isDateTimeInput(targetField) {
    return (
      targetField.type === 'time' ||
      targetField.type === 'date' ||
      targetField.type === 'datetime'
    );
  }

  /**
   * Evaluates the filter expression for a single option of the selectField
   */
  function shouldFilterOption({
    filterExpression,
    optionRow,
    selectField,
    associatedColumnList,
    expressionValues,
    filterCache,
    targetIdentifier,
  }) {
    // ASSOCIATED_COLUMN
    const associatedColumnRegex = /\[ASSOCIATED_COLUMN\]/g;
    let associatedColumnInExpr = filterExpression.match(associatedColumnRegex);
    let semiParsedFilterExpr = filterExpression;

    if (associatedColumnInExpr) {
      // Process each [ASSOCIATED_COLUMN] token sequentially
      for (let i = 0; i < associatedColumnInExpr.length; i++) {
        const associatedColumn = associatedColumnList[i];
        
        if (!associatedColumn) return false;
        
        const associatedCell = associatedColumn.rows[optionRow.row_number];
        
        // Replace only the first occurrence of [ASSOCIATED_COLUMN] in each iteration
        semiParsedFilterExpr = semiParsedFilterExpr.replace(
          /\[ASSOCIATED_COLUMN\]/, 
          `"${associatedCell}"`
        );
      }
    }

    // OPTION_VALUE()
    const optionValueRegex = /OPTION_VALUE\(\)/;
    let optionValueInExpr = semiParsedFilterExpr.match(optionValueRegex);

    while (optionValueInExpr) {
      semiParsedFilterExpr = semiParsedFilterExpr.replace(
        new RegExp(escapeRegExp(optionValueInExpr[0]), 'g'),
        `"${optionRow?.row_value}"`
      );
      optionValueInExpr = semiParsedFilterExpr.match(optionValueRegex);
    }

    if (semiParsedFilterExpr in filterCache) {
      return filterCache[semiParsedFilterExpr];
    }
    
    const evaluatedResult = evaluateExpression({
      values: expressionValues,
      targetIdentifier: targetIdentifier,
      targetField: selectField,
      expression: semiParsedFilterExpr,
    });

    filterCache[semiParsedFilterExpr] = evaluatedResult;

    return evaluatedResult;
  }

  /**
   * Evaluates the filter expression for all options of the select field, then returns an array
   * with true or false values, where true are the options that should be displayed
   */
  const evaluateSelectOptionFilters = useCallback(
    (targetIdentifier, selectField, expressionValues) => {
      const resource =
        selectFieldResourcesRef.current[selectField.options_resource];

      if (!resource) return [];

      const table = resource.find(
        (resourceTable) => resourceTable.table_id === selectField.options_table
      );

      const column = table.columns.find(
        (tableColumn) =>
          tableColumn.column_id === selectField.options_identifier_column
      );

      const optionFilters = [];

      // ASSOCIATED_COLUMN
      const filterExpression = selectField.options_filter_expr;

      const associatedColumnRegex = /ASSOCIATED_COLUMN\("([\w-]+)"\)/gm; //fix DME-1161

      let associatedColumnInExprArr = filterExpression.match(
        associatedColumnRegex
      );
      let semiParsedFilterExpr = filterExpression;

      const associatedColumnList = [];

      //fix DME-1161
      associatedColumnInExprArr.map((associatedColumnInExpr, index) => {
        //get column identifier for string type : ASSOCIATED_COLUMN("f40c4c80-9882-013a-5d6b-784f439d9729")
        const columnIdentifierRegex = /"([\w-]+)"/g;
        const columnIdentifier = associatedColumnInExpr
          .match(columnIdentifierRegex)[0]
          .replace(/["']/g, '');

        let associatedColumn = table.columns.find((columnDef) => {
          return columnDef.column_id === columnIdentifier;
        });

        // Create mutable copy
        associatedColumn = { ...associatedColumn };

        if (!associatedColumn) return false;

        // Create row structure easy to access by row_number so we don't have to use find()
        const rowMap = {};
        Object.values(associatedColumn.rows).forEach((row) => {
          rowMap[row.row_number] = row.row_value;
        });

        associatedColumn.rows = rowMap;

        associatedColumnList.push(associatedColumn);
        semiParsedFilterExpr = semiParsedFilterExpr.replace(
          new RegExp(escapeRegExp(associatedColumnInExpr), 'g'),
          `[ASSOCIATED_COLUMN]`
        );

        const associatedColumnRegexSingle = /ASSOCIATED_COLUMN\("([\w-]+)"\)/;

        associatedColumnInExpr = semiParsedFilterExpr.match(
          associatedColumnRegexSingle
        );
      });

      // Create object to cache expression results so we don't evaluate the same
      // expression many times (useful with big resources)
      const filterCache = {};

      column.rows.forEach((optionRow, index) => {
        const filter = shouldFilterOption({
          filterExpression: semiParsedFilterExpr,
          optionRow,
          selectField,
          associatedColumnList,
          expressionValues,
          filterCache,
          targetIdentifier,
        });
        if (filter) optionFilters[index] = filter;
      });

      return optionFilters;
    },
    [selectFieldResourcesRef.current]
  );

  const processExpressionChangeWithReturn = useCallback(
    function ({ targetIdentifier, targetField, expressionFields = [] }) {
      const repeatGroupFields = repeatGroupFieldsRef.current;
      const groupList = groupListRef.current;

      const evaluatedRules = {};
      const values = {};

      expressionFields.forEach((expressionField) => {
        /**Added another if case for task DME-1933 (case when Group - has value) */
        if (
          expressionField in groupList &&
          groupList[expressionField].length > 0
        ) {
          values[expressionField] = groupList[expressionField].length;
        } else if (!(expressionField in repeatGroupFields)) {
          values[expressionField] = getValues(expressionField) ?? '';
        } else {
          const matchingGroupFields = repeatGroupFields[expressionField].filter(
            (field) => fieldsShareSameParent(field, targetIdentifier)
          );
          values[expressionField] = Object.values(
            getValues(matchingGroupFields)
          );
        }
      });
      // Evaluate all the rules for the target field
      for (let j = 0; j < expressionNames.length; j++) {
        const expressionName = expressionNames[j];
        if (targetField[expressionName]) {
          switch (expressionName) {
            case 'options_filter_expr':
              // Special case for filter expresions
              evaluatedRules.selectOptionFilters = evaluateSelectOptionFilters(
                targetIdentifier,
                targetField,
                values
              );
              break;
            default: {
              const currentExpression = targetField[expressionName];
              const evaluatedResult = evaluateExpression({
                values,
                targetIdentifier,
                targetField,
                expression: currentExpression,
              });
              evaluatedRules[expressionName] = evaluatedResult;
            }
          }
        }
      }

      // Dispatch only 1 action to update the target field
      if (Object.keys(evaluatedRules).length !== 0) {
        const fieldHasBothVisibleCalcExpr =
          targetField.hasOwnProperty('visible_expr') &&
          targetField.hasOwnProperty('calculate_expr');
        const fieldIsNotActive = evaluatedRules.visible_expr === false;
        if (fieldHasBothVisibleCalcExpr && fieldIsNotActive) {
          delete evaluatedRules.calculate_expr;
        }
        return { ...evaluatedRules };
      }
    },
    [groupListRef.current, repeatGroupFieldsRef.current]
  );

  useEffect(
    function evaluateExpressionsOnFormLoad() {
      const rules = Object.values(fieldsWithExpressions).reduce((acc, field) => {
        const { targetField, additionalFields } = field;
        const ruleValue = processExpressionChangeWithReturn({
          targetIdentifier: targetField.identifier,
          targetField,
          expressionFields: additionalFields,
        });
        if (ruleValue !== undefined) {
          acc[targetField.identifier] = ruleValue;
        }
        return acc;
      }, {});
      updateRules(rules);
    },
    [resourceLoadCounter]
  );

  // resources may not be ready yet on page form load, re-evaluate as they come in
  useEffect(
    function evaluateInitialAnswerExpressions() {
      let initialAnswersToUpdate = [];
      let initialAnswersToRemove = [];

      if (renderIterationNumber === 0) {
        setRenderIterationNumber(renderIterationNumber + 1);
        return;
      }

      if (renderIterationNumber < 1) return;

      const repeatGroupFields = repeatGroupFieldsRef.current;

      fieldsWithInitialAnswerExpr.forEach((field) => {
        let isFieldVisible = checkForVisibilityRules(field);

        if (
          field.initialAnswerExpr.includes('INDEX_OF') ||
          field.initialAnswerExpr.includes('COUNT')
        ) {
          handleCountAndIndexOf(field, isFieldVisible, repeatGroupFields, initialAnswersToUpdate, initialAnswersToRemove);
        } else if (field.initialAnswerExpr.includes('INDEX')) {
          handleIndex(field, isFieldVisible, repeatGroupFields, initialAnswersToUpdate, initialAnswersToRemove);
        } else {
          handleOthers(field, isFieldVisible, repeatGroupFields, initialAnswersToUpdate, initialAnswersToRemove);
        }
      });

      if (initialAnswersToUpdate.length > 0) {
        dispatch(updateInitialAnswers(initialAnswersToUpdate));
      }
      if (initialAnswersToRemove.length > 0) {
        dispatch(removeInitialAnswers(initialAnswersToRemove));
      }
    },
    [rules, repeatGroups, refreshedGroupElements, evaluateExpression]
  );

  const handleCountAndIndexOf = (field, isFieldVisible, repeatGroupFields, initialAnswersToUpdate, initialAnswersToRemove) => {
    if (!repeatGroups[field.identifier] || !field.hasOwnProperty('initialAnswerExpr')) return;

    repeatGroups[field.identifier].forEach((targetId) => {
      if (typeof isFieldVisible !== 'undefined' && !isFieldVisible) {
        /**This conidition is used for fields with visibility rules and its replicated in other conditions of this function (DME-1809) */
        initialAnswersToRemove.push({
          identifier: targetId,
        });
        return;
      }
      if (
        targetId &&
        repeatGroupFields.hasOwnProperty(field.identifier) &&
        !initialAnswers.hasOwnProperty(targetId)
      ) {
        let evaluatedResult = evaluateExpression({
          values: {},
          targetIdentifier: targetId,
          targetField: field,
          expression: field.initialAnswerExpr,
        });

        // DME-1837 A better aproach to find if an expression fulfills the requirements to be considered as a special case for this ticket.
        // The idea behind this is to make sure that the expressions fulfills a certain pattern expression (which is shown below)
        let fulfillsExpressionTemplate =
          expressionFulfillsPatternTemplate(
            field.initialAnswerExpr,
            'SUM(::FIELD::[COUNT(::FIELD::)'
          );

        if (fulfillsExpressionTemplate) {
          // DME-1837 work
          // this handles expressions like this "SUM(.Group[COUNT(.Group) - 2]/Integer_Question,1)"
          // this expression calculates the index of the group in order to fill the field value with the groups index
          const groupIndex =
            targetId.match(/\d+/)[0] < 0 ? 0 : targetId.match(/\d+/)[0];
          const groupIndexFromRepeatGroupFields = repeatGroupFields[field.identifier].indexOf(targetId);
          const groupIndexFromInitAnsweExpr = repeatGroups[field.identifier].indexOf(targetId);

          if (
            groupIndexFromRepeatGroupFields >= 0 &&
            groupIndexFromInitAnsweExpr >= 0
          ) {
            // this initializes field values for fields which where dispatched=
            initialAnswersToUpdate.push({
              identifier: targetId,
              value: groupIndex,
            });
          } else {
            // this initialiazes field values for new repeat group fields=
            initialAnswersToUpdate.push({
              identifier: targetId,
              value: evaluatedResult,
            });
          }
        } else {
          initialAnswersToUpdate.push({
            identifier: targetId,
            value: evaluatedResult,
          });
        }
      }
    });
  }

  const handleIndex = (field, isFieldVisible, repeatGroupFields, initialAnswersToUpdate, initialAnswersToRemove) => {
    if (repeatGroups[field.identifier]) {
      // repeat group
      repeatGroups[field.identifier] &&
        repeatGroups[field.identifier].forEach((targetId) => {
          if (
            typeof isFieldVisible !== 'undefined' &&
            !isFieldVisible
          ) {
            /**This conidition is used for fields with visibility rules and its replicated in other conditions of this function (DME-1809) */
            initialAnswersToRemove.push({
              identifier: targetId,
            });
            return;
          }

          if (
            targetId &&
            repeatGroupFields.hasOwnProperty(field.identifier) &&
            !initialAnswers.hasOwnProperty(targetId)
          ) {
            const groupPart = targetId.split('/')[0];
            let exprInitialAnswers = {};

            const exprFields = getExpressionFields(
              field.initialAnswerExpr
            );
            exprFields.forEach((id, i) => {
              const fields = repeatGroupFields[id];
              const selectedField = fields.filter((fieldId) =>
                fieldId.startsWith(groupPart)
              )[0];
              if (selectedField) {
                let currentInput = getInput(selectedField) ?? {};
                const fieldCurrentValue =
                  getValues(selectedField) ?? '';
                if (Object.keys(currentInput).length) return;

                if (
                  fieldCurrentValue !== undefined &&
                  fieldCurrentValue !== '' &&
                  currentInput?.type !== 'datetime'
                ) {
                  exprInitialAnswers = {
                    ...exprInitialAnswers,
                    [selectedField]: fieldCurrentValue,
                    [id]: fieldCurrentValue,
                  };
                } else if (
                  !('initialAnswer' in currentInput) &&
                  !('initialAnswerExpr' in currentInput)
                ) {
                  exprInitialAnswers = {
                    ...exprInitialAnswers,
                    [selectedField]: '',
                    [id]: '',
                  };
                } else if (currentInput?.initialAnswer) {
                  exprInitialAnswers = {
                    ...exprInitialAnswers,
                    [selectedField]: currentInput?.initialAnswer ?? '',
                    [id]: currentInput?.initialAnswer ?? '',
                  };
                }
              }
            });

            if (
              typeof isFieldVisible !== 'undefined' &&
              !isFieldVisible
            )
              return;

            const evaluatedResult = evaluateExpression({
              values: exprInitialAnswers,
              targetIdentifier: targetId,
              targetField: field,
              expression: field.initialAnswerExpr,
              isInitialAnswer: true,
            });
            initialAnswersToUpdate.push({
              identifier: targetId,
              value: evaluatedResult,
            });
          }
        });
    } else {
      if (typeof isFieldVisible !== 'undefined' && !isFieldVisible) {
        initialAnswersToRemove.push({
          identifier: field.identifier,
        });
        return;
      }
      // non repeat group
      const exprFields = getExpressionFields(field.initialAnswerExpr);
      const exprInitialAnswers = {};
      exprFields.forEach((id) => {
        const currentInput = getInput(id);
        if (currentInput !== undefined) {
          const fieldCurrentValue = getValues(id) ?? '';
          if (
            fieldCurrentValue !== undefined &&
            fieldCurrentValue !== '' &&
            currentInput.type !== 'datetime'
          ) {
            exprInitialAnswers[id] = fieldCurrentValue;
          } else if (
            !('initialAnswer' in currentInput) &&
            !('initialAnswerExpr' in currentInput)
          ) {
            exprInitialAnswers[id] = '';
          } else if (currentInput.initialAnswer) {
            exprInitialAnswers[id] = currentInput.initialAnswer;
          }
        }
      });

      const evaluatedResult = evaluateExpression({
        values: exprInitialAnswers,
        targetIdentifier: field.identifier,
        targetField: field,
        expression: field.initialAnswerExpr,
      });
      initialAnswersToUpdate.push({
        identifier: field.identifier,
        value: evaluatedResult,
      });
    }
  }

  const handleOthers = (field, isFieldVisible, repeatGroupFields, initialAnswersToUpdate, initialAnswersToRemove) => {
    const exprFields = getExpressionFields(field.initialAnswerExpr);

    /** Added refreshedGroupElements in order for new group fields to be checked for initialAnswerExpressions */
    let targetId = refreshedGroupElements.find(
      (el) => field.identifier === getIdWithoutSuffix(el.identifier)
    )?.identifier;

    const exprInitialAnswers = {};

    exprFields.forEach((id) => {
      const currentInput = getInput(id);
      if (currentInput !== undefined) {
        let fieldCurrentValue = getValues(id) ?? '';

        if (targetId) {
          const matchingGroupFields = repeatGroupFields[id]?.filter(
            (field) => fieldsShareSameParent(field, targetId)
          );
          if (matchingGroupFields) {
            fieldCurrentValue =
            Object.values(getValues(matchingGroupFields)) ?? '';
          }
        }

        if (
          fieldCurrentValue !== undefined &&
          fieldCurrentValue !== '' &&
          currentInput.type !== 'datetime'
        ) {
          exprInitialAnswers[id] = fieldCurrentValue;
        } else if (
          !('initialAnswer' in currentInput) &&
          !('initialAnswerExpr' in currentInput)
        ) {
          exprInitialAnswers[id] = '';
        } else if (currentInput.initialAnswer) {
          exprInitialAnswers[id] = currentInput.initialAnswer;
        }
      }
    });

    if (targetId) {
      let isGroupFieldVisible = checkForVisibilityRules({
        identifier: targetId,
      });
      if (
        typeof isGroupFieldVisible !== 'undefined' &&
        !isGroupFieldVisible
      ) {
        initialAnswersToRemove.push({
          identifier: targetId,
        });
        return;
      }

      if (
        Object.values(exprInitialAnswers).every(
          (v) => v !== undefined
        ) &&
        targetId &&
        repeatGroupFields.hasOwnProperty(field.identifier) &&
        !initialAnswers.hasOwnProperty(targetId) &&
        !field.initialAnswerExpr.includes('INDEX')
      ) {
        const currentEl = refreshedGroupElements.filter(
          (item) => item.identifier === targetId
        )[0];
        if (currentEl.type === 'text' && currentEl.initialAnswer) {
          // FIX DM-1725
          // this will enter here only if the currentEl identifier is equal to the targetId
          // and if the currentEl is of type text
          // and if the currentEl initialAnswer is not undefined/null
          // this will make the expression evaluator on fields which have no initialAnswer
          // since the evaluateExpression evaluatated the values of these fields based on other repeat group values
          const evaluatedResult = evaluateExpression({
            values: exprInitialAnswers,
            targetIdentifier: targetId,
            targetField: field,
            expression: field.initialAnswerExpr,
          });
          initialAnswersToUpdate.push({
            identifier: targetId,
            value: evaluatedResult,
          });
        } else {
          const evaluatedResult = evaluateExpression({
            values: exprInitialAnswers,
            targetIdentifier: targetId,
            targetField: field,
            expression: field.initialAnswerExpr,
          });
          initialAnswersToUpdate.push({
            identifier: targetId,
            value: evaluatedResult,
          });
        }
      }
    } else {
      /** Enters here for other elements */
      if (typeof isFieldVisible !== 'undefined' && !isFieldVisible) {
        initialAnswersToRemove.push({
          identifier: field.identifier,
        });
        return;
      }

      if (
        Object.values(exprInitialAnswers).every(
          (v) => v !== undefined
        ) &&
        !initialAnswers.hasOwnProperty(field.identifier)
      ) {
        const evaluatedResult = evaluateExpression({
          values: exprInitialAnswers,
          targetField: field,
          expression: field.initialAnswerExpr,
        });
        initialAnswersToUpdate.push({
          identifier: field.identifier,
          value: evaluatedResult,
        });
      }
    }
  }
  
  useEffect(
    function evaluateFilterExpressionsOnResourceLoad() {
      const rules = Object.values(fieldsWithExpressions).reduce((acc, field) => {
        const { targetField, additionalFields } = field;
  
        if (targetField.options_filter_expr) {
          const ruleValue = processExpressionChangeWithReturn({
            targetIdentifier: targetField.identifier,
            targetField,
            expressionFields: additionalFields,
          });
          if (ruleValue !== undefined) {
            acc[targetField.identifier] = ruleValue;
          }
        }
        return acc;
      }, {});
      updateRules(rules);
    },
    [selectFieldResourcesRef.current]
  );

  const evaluateExpressionChanges = useCallback(
    (identifier) => {
      const rules = {};
      const identifierWithoutSuffix = getIdWithoutSuffix(identifier);
      if (fieldsToWatchMap[identifierWithoutSuffix] !== undefined) {
        const relatedFields = fieldsToWatchMap[identifierWithoutSuffix];
        for (let i = 0; i < relatedFields.length; i++) {
          let ruleValue;
          const { targetField, additionalFields } = relatedFields[i];
          const repeatGroupFields = repeatGroupFieldsRef.current;

          if (targetField) {
            if (repeatGroupFields[targetField.identifier]) {
              repeatGroupFields[targetField.identifier].forEach(
                (targetIdentifier) => {
                  ruleValue = processExpressionChangeWithReturn({
                    targetIdentifier,
                    targetField,
                    expressionFields: additionalFields,
                  });
                  if (ruleValue !== undefined) {
                    rules[targetIdentifier] = ruleValue;
                  }
                }
              );
            } else {
              //Checking if targetField.identifier matches the group to add a rule for the specific group
              const extractMatchingGroupKeys = Object.keys(
                groupsRef.current
              )?.filter(
                (key) => getIdWithoutSuffix(key) === targetField.identifier
              );
              if (extractMatchingGroupKeys) {
                extractMatchingGroupKeys.forEach((groupId) => {
                  let ruleValue = processExpressionChangeWithReturn({
                    targetIdentifier: groupId,
                    targetField: targetField,
                    expressionFields: additionalFields,
                  });
                  if (ruleValue !== undefined) {
                    rules[groupId] = ruleValue;
                  }
                });
              }
              //Checking if targetField.identifier matches the subform to add a rule for the specific subform
              if (subformsRef.current.hasOwnProperty(targetField.identifier)) {
                subformsRef.current[targetField.identifier].forEach(
                  (subformId) => {
                    let ruleValue = processExpressionChangeWithReturn({
                      targetIdentifier: subformId,
                      targetField: targetField,
                      expressionFields: additionalFields,
                    });
                    if (ruleValue !== undefined) {
                      rules[subformId] = ruleValue;
                    }
                  }
                );
              }
              ruleValue = processExpressionChangeWithReturn({
                targetIdentifier: targetField.identifier,
                targetField,
                expressionFields: additionalFields,
              });
              if (ruleValue !== undefined) {
                rules[targetField.identifier] = ruleValue;
              }
            }
          }
        }
      }
      updateRules(rules);
    },
    [
      repeatGroupFieldsRef.current,
      processExpressionChangeWithReturn,
      subformsRef.current,
    ]
  );

  const updateGroupElementExpressions = useCallback(
    (groupElements) => {
      const rules = {};
      // const fieldsToWatch = Object.keys(fieldsToWatchMap);
      /**
       * refreshedGroupElementsArray always gets the last groups added to the form. It's needed to add initialAnswers (evaluateInitialAnswerExpressions above) in redux
       * and also set it as a value in react-hook-forms
       */
      let refreshedGroupElementsArray = [];

      function iterateGroupElements(element) {
        if (element.children) {
          /**
           * Adding a check here for visibility group rules. If a group with expression is found, then it evaluates that rule.
           */
          const fieldWithExpressions =
            fieldsWithExpressions[getIdWithoutSuffix(element.identifier)];
          if (fieldWithExpressions) {
            const { targetField, additionalFields } = fieldWithExpressions;
            let ruleValue = processExpressionChangeWithReturn({
              targetIdentifier: element.identifier,
              targetField: targetField,
              expressionFields: additionalFields,
            });
            if (ruleValue !== undefined) {
              rules[element.identifier] = ruleValue;
            }
          }

          element.children.forEach((currentElement) =>
            iterateGroupElements(currentElement)
          );
          refreshedGroupElementsArray.push(element);
        } else {
          if (element.initialAnswerExpr) {
            refreshedGroupElementsArray.push(element);
          }
          const fieldWithExpressions =
            fieldsWithExpressions[getIdWithoutSuffix(element.identifier)];

          if (fieldWithExpressions) {
            const { targetField, additionalFields } = fieldWithExpressions;
            let ruleValue = processExpressionChangeWithReturn({
              targetIdentifier: element.identifier,
              targetField,
              expressionFields: additionalFields,
            });
            if (ruleValue !== undefined) {
              rules[element.identifier] = ruleValue;
            }
          }
        }
      }

      groupElements.forEach((element) => {
        iterateGroupElements(element);
      });
      setRefreshedGroupElements(refreshedGroupElementsArray);
      updateRules(rules);
    },
    [fieldsWithExpressions, processExpressionChangeWithReturn]
  );

  /**This function can be changed in the future adding other conditions*/
  const checkForVisibilityRules = (field) => {
    let filterVisibility;
    if (Object(rules[field.identifier]).hasOwnProperty('visible_expr')) {
      filterVisibility = rules[field.identifier]['visible_expr'];
    }
    return filterVisibility;
  };

  return [
    evaluateExpressionChanges,
    evaluateExpression,
    updateGroupElementExpressions,
  ];
}

/**
 * Extracts the name of the function that is wrapping a field
 *
 * Example
 *
 * Input params:
 *
 * - expression: "SUM(AVERAGE(5, 15), Group.Integer_Question)"
 * - currentFieldName: "Group.Integer_Question"
 *
 * Output: "SUM"
 *
 * @param {string} expression
 * @param {string} currentFieldName
 * @returns String or null
 */
const getCurrentWrapperFunction = (newExpression, currentFieldName) => {
  const currentFieldIndex = newExpression.indexOf(currentFieldName);

  if (currentFieldIndex === -1) return null;

  let expressionBeforeField = newExpression.slice(0, currentFieldIndex);
  if (expressionBeforeField.includes('[')) {
    expressionBeforeField = expressionBeforeField.split('[')[1];
  }
  if (expressionBeforeField.includes(']')) {
    expressionBeforeField = expressionBeforeField.split(']')[0];
  }
  let openingParenthesis = 0;
  let expressionBeforeOpeningParenthesis;

  for (let i = expressionBeforeField.length; i >= 0; i--) {
    const currentChar = expressionBeforeField[i];

    if (currentChar === ')') openingParenthesis -= 1;
    if (currentChar === '(') openingParenthesis += 1;

    if (openingParenthesis === 1) {
      expressionBeforeOpeningParenthesis = expressionBeforeField.slice(0, i);
      break;
    }
  }
  if (!expressionBeforeOpeningParenthesis) return null;

  const splittedExpression = splitBySeparators(
    expressionBeforeOpeningParenthesis
  );
  return splittedExpression[splittedExpression.length - 1];
};

/**
 * Applies filters to an expression value coming from a field
 */
const filterExpressionValue = (
  value,
  { shouldKeepStringFormatting = false } = {}
) => {
  let newValue = value;

  if (typeof newValue === 'object') {
    newValue = newValue[0] ? newValue[0] : '';
  }

  if (
    typeof newValue === 'string' &&
    (shouldKeepStringFormatting || !isNumber(newValue))
  ) {
    // Wrap string with double quotes.
    // If the string contains double quotes, they will be escaped with a backslash
    // Also backslashes will be escaped

    const escapedString = newValue.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

    newValue = `"${escapedString}"`;
  }

  if (newValue === '' || newValue === undefined) {
    newValue = `""`;
  }

  return newValue;
};

/**
 * Process result value and apply some filters to it
 * @param {String} result
 */
const processResultValue = (result, typeDerived) => {
  if (result === 'true') return true;
  if (result === 'false') return false;

  // Catchs null, undefined, and NaN values
  if (result === null || result === undefined || Number.isNaN(result)) {
    return '';
  }

  if (typeof result === 'string') {
    // Remove double quotes that are wrapping the string
    // Remove double quote escaping
    // Remove backslash escaping
    return result
      .replace(/^"|"$/g, '')
      .replace(/\\"/g, `"`)
      .replace(/\\\\/g, `\\`);
  }

  // Convert date object to date string
  if (result instanceof Date) {
    // Check if the date is invalid
    if (Number.isNaN(result.getTime())) {
      return '';
    }
    return getFormattedDate(result, typeDerived);
  }

  return result;
};

export default useExpression;


